<template>
    <div>
        <!-- Espace du dashboard -->
        <grid-layout
            :id="dashboardTypeEnum[dashboardType].toString() + lDashboard.Id"
            :ref="dashboardTypeEnum[dashboardType].toString() + lDashboard.Id"
            :layout.sync="lDashboard.Widgets"
            :col-num="colNum"
            :row-height="30"
            :is-draggable="true"
            :is-resizable="true"
            :is-mirrored="false"
            :margin="[10, 10]"
            :use-css-transforms="false"
            :responsive="($screen.breakpoint === 'xs' || $screen.breakpoint === 'sm')"
            :vertical-compact="false"
            :prevent-collision="true"
            :autoSize="true"
            :class="['h-100 w-100 bg-white', { 'vue-grid-layout-edit': lDashboard.EditMode }]">
            
            <!-- Si des widgets existent sur le tableau de bord -->
            <template v-if="lDashboard && lDashboard.Widgets && lDashboard.Widgets.length > 0">
                <grid-item :ref="dashboardType.toString() + lDashboard.Id + 'widget' + widget.i" v-for="widget in lDashboard.Widgets" :key="widget.Id" 
                    :x="widget.x" :y="widget.y" :w="widget.w" :h="widget.h" :i="widget.i" :static="!lDashboard.EditMode"
                    :minW="widgetLibrary(widgetTypeEnum[widget.Type])?.MinW"
                    :minH="widget.minH" :maxH="widget.maxH"
                    drag-allow-from=".vue-draggable-handle"
                    drag-ignore-from=".no-drag">

                    <!-- Container de widget -->
                    <WidgetContainer :DashboardId="lDashboard.Id" :WidgetId="widget.Id" :Title="widget.Name" :Height="widget.h" 
                        :WidgetLibrary="widgetLibrary(widgetTypeEnum[widget.Type])"
                        :TargetDashboard="dashboardType" :Parameters="widget.Parameters" :InError="widget.InError"
                        :Action="getAction()"
                        :AutoSize="true" :EditMode="lDashboard.EditMode" :DisplayConfigButton="true" :DisplayExportButton="true" :DisplayNodesConfig="true"
                        @RemoveWidgetEvent="removeWidget(widget.i)" />
                </grid-item>
            </template>

            <!-- Rendu lorsqu'aucun widget n'est présent sur le tableau de bord -->
            <grid-item v-else :x=0 :y=0 :w=2 :h=6 :i="1" :key="1" static class="rounded" :style="'border: 2px dashed var(--' + Variant + ');'">
                <b-container class="h-100">
                    <b-row class="justify-content-center h-100 align-items-center"><b-col class="align-self-center">
                        <b-row><b-col class="text-center text-gray-dark"><b-icon icon="grid" font-scale="2" /></b-col></b-row>
                        <b-row><b-col class="text-center py-3 px-5 text-gray-dark">{{ $t('AddFirstWidget') }}</b-col></b-row>
                    </b-col></b-row>
                </b-container>
            </grid-item>
        </grid-layout>

        <!-- 
            Bibliothèque de widgets
            Remarque : 'MainDashboards__BV_tab_container_' passé à la prop Top correspond au container de la liste des onglets de MainDashboards.vue pour obtenir sa hauteur et placer la bibliothèque de widgets en conséquence
         -->
        <WidgetLibrary v-if="displayWidgetsLib" v-model="selectedWidget" :Right="true" :DisplayWidgetsLib="displayWidgetsLib" :DashboardType="dashboardType" :Top="getTop('MainDashboards__BV_tab_container_')"
            @AddWidgetEvent="addWidget(false)" @DragStartEvent="dragstart" @DragEvent="drag" @DragEndEvent="dragend" />

        <!-- Barre d'outils -->
        <portal to="MDashboardsToolbarPortal" v-if="lDashboard.Id === ActiveDashboardId">
            <!-- Ajout de widgets -->
            <b-button :id="'btnAddWidget' + lDashboard.Id" v-if="lDashboard.EditMode && $store.getters['CheckAction']('Add', 'MainDashboard')" @click="displayWidgetsLib = !displayWidgetsLib" v-b-tooltip.hover :title="$t('AddWidget')" :variant="Variant" pill class="mx-2">
                <b-icon icon="plus-lg" class="pr-1" />{{ $t('Widget') }}
            </b-button>
            
            <!-- Edition du tableau de bord -->
            <b-button v-if="$store.getters['CheckAction']('Write', 'MainDashboard')" @click="toggleEditMode(lDashboard.Id)" v-b-tooltip.hover :title="$t('Edition')" :variant="'outline-' + Variant" class="btn-circle border-0 px-2 mx-2">
                <b-icon :icon="'pencil' + (lDashboard.EditMode ? '-fill' : '')" />
            </b-button>
        </portal>

        <!-- Fenêtre modale de renommage du tableau de bord -->
        <b-modal :id="'tabRenameModal' + lDashboard.Id" :title="$t('NewName')" centered>
            <b-form-input type="text" v-model="tabName" />
            <template #modal-footer="{ cancel }">
                <b-button :variant="'outline-' + Variant" pill @click="cancel();">{{ $t('Cancel') }}</b-button>
                <b-button :variant="Variant" pill @click="rename(); cancel();">{{ $t('Save1') }}</b-button>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Watch } from 'vue-property-decorator';
    import TMainDashboard from '@/types/Dashboard/MainDashboard/MainDashboard';
    import DashboardTypeEnum from '@/enums/DashboardTypeEnum';
    import Dashboard from "@/components/commons/Dashboard";
    import WidgetLibrary from '@/types/Dashboard/WidgetLibrary';
    import locales from './locales/MainDashboard.json';

    /** Tableau de bord principal */
    @Component({
        i18n : { messages : locales }
    })
    export default class MainDashboard extends Dashboard<TMainDashboard> {
        /** Id du tableau de bord actif */
		@Prop()
		public ActiveDashboardId!: number;

        /** Récupère l'action du tableau de bord en cours */
        private getAction() {
            return this.$store.getters['GetMasterAction'](this.dashboardTypeEnum[this.dashboardType].toString() + 'Dashboard');
        }

        /** Bascule entre le mode lecteur et édition sur le tableau de bord en cours */
        private toggleEditMode(dashboardId) {
            console.debug("MainDashboard - toggleEditMode - dashboardId : ", dashboardId);
            this.$store.dispatch('MainDashboardStore/ToggleEditMode', dashboardId);
        }

        /** Type de dashboard pour spécifié avec quel store VueX communiquer dans la classe heritée Dashboard */
        protected readonly dashboardType: DashboardTypeEnum = DashboardTypeEnum.Main;

        /** Tableau de bord récupéré du server */
        @Prop()
        public PMainDashboard!: TMainDashboard;

        /** Chargement local du MainDashboard passé en prop (nécessaire pour pouvoir le modifier ==> Prop non mutable) */
        @Watch('PMainDashboard', { immediate: true, deep: true})
        private loadMainDashboard() {
            if (this.PMainDashboard)
                this.lDashboard = this.PMainDashboard;
        }
        
        //#region Renommage du tableau de bord
        /** Nom de tableau de bord saisi */
        private tabName: string = "";

        /** Enregistrement du tableau de bord principal courant (réalisé uniquement en mode Edition) */
        @Watch('lDashboard.EditMode')
        private save(newValue = undefined, oldValue = undefined) {
            // Fermeture de la bibliothèque de widgets
            this.displayWidgetsLib = false;

            console.debug("MainDashboard - save() - EditMode : %O - this.renameMode : %O - newValue : %O - oldValue : %O", this.lDashboard.EditMode, this.renameMode, newValue, oldValue);

            // Sur sortie du mode Edition : le tableau de bord est enregistré
            //if (((oldValue && oldValue === true) && (newValue === undefined || newValue === false)) || this.renameMode)
            if ((oldValue && !newValue) || this.renameMode)
                return this.$store.dispatch("MainDashboardStore/Save", { dashboard: this.lDashboard });
        }

        /** Indique si l'on est en cours de renommage ou non */
        private renameMode: boolean = false;

        /** 
         * Renomme le tableau de bord principal courant
         * @todo : créer méthode juste pour rename au lieu de tout sauvegarder inutilement (les widgets...)...
         */
        private rename() {
            console.debug("MainDashboard - rename()");

            this.lDashboard.Name = this.tabName;

            this.renameMode = true;

            /** @todo : créer méthode juste pour rename au lieu de tout sauvegarder inutilement (les widgets...)... */
            // Enregistrement du tableau de bord
            this.save().then(() => {
                    this.$bvToast.toast(this.$t('DashboardRenamed').toString(), {
                    title: this.$t('Information').toString(),
                    autoHideDelay: 5000,
                    variant: "success",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            })
            .catch(err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccurred').toString(), {
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
            })
            .finally(() => {
                this.renameMode = false;
            });
        }
        //#endregion Renommage du tableau de bord

        created () {
            this.tabName = this.lDashboard.Name;

            /** Indique que le tableau de bord est automatiquement sauvegardé en bdd après l'ajout et la supression d'un widget */
            this.autoSave = true;
        }
    }
</script>

<style scoped>
    /** Styles utilisés par le Vue-Grid-Layout définis dans App.vue */

    /** Redéfini la couleur d'ombre derrière le déplacement d'un widget */
    :deep(.container .vue-grid-item.vue-grid-placeholder) {
        background: var(--primary) !important;
    }

    :deep(.vue-grid-item.vue-grid-placeholder) {
        background: var(--primary) !important;
    }

    /** Pour mode édition */
    .vue-grid-layout-edit {
        transition: none;
    }
</style>